import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AnimatePresence, m } from 'framer-motion';
import Iconify from 'src/components/iconify';
import type {
  BenefitBulkEditChange,
  BenefitBulkEditPhase,
  BenefitBulkEditStatus,
} from '../benefits-list.hook';

type BenefitsBulkEditPanelProps = {
  items: BenefitBulkEditChange[];
  statuses: Record<string, BenefitBulkEditStatus>;
  messages: Record<string, string>;
  phase: BenefitBulkEditPhase;
  invalidItemIds: string[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onRemoveItem: (benefitId: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  onFinish: () => void;
};

const statusIconMotion = {
  initial: { opacity: 0, scale: 0.72, y: 3 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.72, y: -3 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

const getPanelTitle = (phase: BenefitBulkEditPhase, totalItems: number, completedItems: number) => {
  if (phase === 'submitting') {
    return `Salvando ${completedItems} de ${totalItems}`;
  }

  if (phase === 'completed') {
    return `${completedItems} de ${totalItems} concluídas`;
  }

  return `${totalItems} ${totalItems === 1 ? 'alteração pendente' : 'alterações pendentes'}`;
};

export default function BenefitsBulkEditPanel({
  items,
  statuses,
  messages,
  phase,
  invalidItemIds,
  collapsed,
  onToggleCollapsed,
  onRemoveItem,
  onCancel,
  onSubmit,
  onFinish,
}: BenefitsBulkEditPanelProps) {
  const invalidItems = new Set(invalidItemIds);
  const completedItems = items.filter((item) => statuses[item.id] === 'success').length;
  const warningItems = items.filter((item) => statuses[item.id] === 'warning').length;
  const failedItems = items.filter((item) => statuses[item.id] === 'error').length;
  const processedItems = completedItems + warningItems + failedItems;
  const progress = items.length > 0 ? Math.round((processedItems / items.length) * 100) : 0;
  const isSubmitting = phase === 'submitting';
  const isCompleted = phase === 'completed';

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    if (isCompleted) {
      onFinish();
      return;
    }

    onCancel();
  };

  return (
    <Paper
      component={m.div}
      elevation={16}
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
      sx={(theme) => ({
        position: 'fixed',
        zIndex: theme.zIndex.snackbar,
        right: { xs: 12, sm: 24 },
        left: 'auto',
        bottom: { xs: 12, sm: 24 },
        width: { xs: 'calc(100vw - 24px)', sm: 300 },
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.5),
        borderRadius: 2,
        boxShadow: `0 18px 48px ${alpha(theme.palette.grey[900], 0.24)}`,
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minHeight: 50, px: 1.5, py: 0.75 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {getPanelTitle(phase, items.length, completedItems + warningItems)}
          </Typography>
          {(isSubmitting || isCompleted) && (
            <Typography variant="caption" color="text.secondary">
              {progress}% concluído
            </Typography>
          )}
        </Box>

        <Tooltip title={collapsed ? 'Expandir' : 'Recolher'}>
          <IconButton size="small" onClick={onToggleCollapsed} aria-label="Recolher painel">
            <Iconify
              icon={collapsed ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              sx={{ width: 22, height: 22 }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title={isSubmitting ? 'Aguarde o envio terminar' : 'Fechar'}>
          <span>
            <IconButton
              size="small"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Fechar painel de edição"
            >
              <Iconify icon="mdi:close" sx={{ width: 21, height: 21 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {(isSubmitting || isCompleted) && (
        <LinearProgress
          variant="determinate"
          value={progress}
          color={failedItems > 0 ? 'warning' : 'primary'}
          sx={{ height: 4 }}
        />
      )}

      <Collapse in={!collapsed} timeout="auto">
        <Box
          sx={{
            maxHeight: 240,
            overflowY: 'auto',
            borderTop: isSubmitting || isCompleted ? 0 : '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <AnimatePresence mode="popLayout">
            {items.map((item, index) => {
              const status = statuses[item.id] || 'pending';
              const isInvalid = invalidItems.has(item.id);
              let statusOpacity = 1;

              if (phase !== 'editing' && status === 'pending') {
                statusOpacity = 0.72;
              }

              if (status === 'loading') {
                statusOpacity = 0.58;
              }

              return (
                <m.div
                  key={item.id}
                  layout="position"
                  initial={{ opacity: 0, x: 16, scale: 0.98 }}
                  animate={{ opacity: statusOpacity, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.32, ease: 'easeOut' }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{
                      minHeight: 54,
                      px: 1.5,
                      py: 0.75,
                      borderBottom: index === items.length - 1 ? 0 : '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        display: 'grid',
                        flexShrink: 0,
                        width: 38,
                        height: 38,
                        overflow: 'hidden',
                        placeItems: 'center',
                        border: '1px solid',
                        borderColor: isInvalid ? 'error.light' : 'grey.300',
                        borderRadius: 1,
                        color: isInvalid ? 'error.main' : 'text.disabled',
                        bgcolor: isInvalid
                          ? alpha(theme.palette.error.main, 0.08)
                          : alpha(theme.palette.grey[500], 0.08),
                      })}
                    >
                      {item.image ? (
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.name || 'Benefício'}
                          sx={{ display: 'block', width: 1, height: 1, objectFit: 'cover' }}
                        />
                      ) : (
                        <Iconify icon="mdi:image-off-outline" sx={{ width: 19, height: 19 }} />
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Tooltip title={item.name || 'Nome não preenchido'} placement="top-start">
                        <Typography
                          variant="body2"
                          noWrap
                          color={isInvalid ? 'error.main' : 'text.primary'}
                          sx={{ fontWeight: 600 }}
                        >
                          {item.name || 'Nome não preenchido'}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        Prioridade: {item.priority ?? 'Sem prioridade'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        flexShrink: 0,
                        width: 30,
                        height: 30,
                        placeItems: 'center',
                      }}
                    >
                      <AnimatePresence initial={false} mode="wait">
                        {phase === 'editing' && (
                          <m.div key="delete" {...statusIconMotion} style={{ display: 'flex' }}>
                            <Tooltip title="Descartar alteração">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onRemoveItem(item.id)}
                                aria-label={`Descartar alteração de ${item.name}`}
                              >
                                <Iconify
                                  icon="mdi:trash-can-outline"
                                  sx={{ width: 19, height: 19 }}
                                />
                              </IconButton>
                            </Tooltip>
                          </m.div>
                        )}

                        {phase !== 'editing' && status === 'pending' && (
                          <m.div key="pending" {...statusIconMotion} style={{ display: 'flex' }}>
                            <Tooltip title="Aguardando o próximo lote">
                              <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                                <Iconify icon="mdi:clock-outline" sx={{ width: 21, height: 21 }} />
                              </Box>
                            </Tooltip>
                          </m.div>
                        )}

                        {status === 'loading' && (
                          <m.div key="loading" {...statusIconMotion} style={{ display: 'flex' }}>
                            <CircularProgress size={20} thickness={5} />
                          </m.div>
                        )}

                        {status === 'success' && (
                          <m.div key="success" {...statusIconMotion} style={{ display: 'flex' }}>
                            <Tooltip title="Alteração concluída">
                              <Box sx={{ display: 'flex', color: 'success.main' }}>
                                <Iconify icon="mdi:check-circle" sx={{ width: 22, height: 22 }} />
                              </Box>
                            </Tooltip>
                          </m.div>
                        )}

                        {status === 'warning' && (
                          <m.div key="warning" {...statusIconMotion} style={{ display: 'flex' }}>
                            <Tooltip title={messages[item.id] || 'Alteração concluída com aviso de sincronização'}>
                              <Box sx={{ display: 'flex', color: 'warning.main' }}>
                                <Iconify icon="mdi:alert-circle" sx={{ width: 22, height: 22 }} />
                              </Box>
                            </Tooltip>
                          </m.div>
                        )}

                        {status === 'error' && (
                          <m.div key="error" {...statusIconMotion} style={{ display: 'flex' }}>
                            <Tooltip title={messages[item.id] || 'Não foi possível concluir esta alteração'}>
                              <Box sx={{ display: 'flex', color: 'error.main' }}>
                                <Iconify icon="mdi:alert-circle" sx={{ width: 22, height: 22 }} />
                              </Box>
                            </Tooltip>
                          </m.div>
                        )}
                      </AnimatePresence>
                    </Box>
                  </Stack>
                </m.div>
              );
            })}
          </AnimatePresence>
        </Box>

        <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'background.paper' }}>
          {phase === 'editing' && invalidItemIds.length > 0 && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
              Preencha o nome e informe uma prioridade numérica válida antes de salvar.
            </Typography>
          )}

          {phase === 'editing' && (
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button size="small" color="inherit" onClick={onCancel}>
                Cancelar edição
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={onSubmit}
                disabled={items.length === 0 || invalidItemIds.length > 0}
              >
                Salvar alterações
              </Button>
            </Stack>
          )}

          {isSubmitting && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} thickness={5} />
              <Typography variant="caption" color="text.secondary">
                Não feche ou atualize esta página durante o envio.
              </Typography>
            </Stack>
          )}

          {isCompleted && (
            <Stack direction="row" justifyContent="flex-end">
              <Button size="small" variant="contained" onClick={onFinish}>
                Concluir
              </Button>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
